import styled from "@emotion/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { Fragment, memo, MouseEventHandler, ReactNode, useState } from "react";
import Truncate from "react-truncate-markup";

import RichTextViewerDefaultEllipsis from "~/components/common/RichTextViewer/RichTextViewerDefaultEllipsis";
import { HASH_TAG_SYMBOL, USER_NICKNAME_PREFIX } from "~/constants/common";
import { EXTERNAL_LINK_REL_VALUE, LinkRelValue } from "~/constants/link";
import {
  EXTERNAL_LINK_TYPES,
  INTERNAL_LINK_TYPES,
  RICH_TEXT_PART_TYPENAME
} from "~/constants/richText";
import { DTORichText } from "~/declarations/graphql/types";
import { isString } from "~/utils/common";
import notEmpty from "~/utils/notEmpty";
import getNormalizedRichText from "~/utils/richText/getNormalizedRichText";
import { getSeparatedTextAndEmojis } from "~/utils/richText/getSeparatedTextAndEmojis";
import isTextStandaloneEmoji from "~/utils/richText/isTextStandaloneEmoji";

import Emoji from "../Emoji";
import WithLink from "../WithLink";
import {
  getMentionLinkProps,
  getNormalizedAbsoluteUrl,
  getTagLinkProps
} from "./utils";

const DEFAULT_VISIBLE_LINES_COUNT = 2;
const EMOJI_SIZE_LARGE = 40;

interface Props {
  richText: DTORichText; // TODO: 1655644151 sometimes we need to know in advance whether a block with text will be render,
  // therefore it is proposed to create a branded NotEmptyRichText type, so all checks for emptiness will be outside the component
  className?: string;
  ellipsis?: ReactNode;
  expandable?: boolean;
  showEllipsis?: boolean;
  initialCollapsed?: boolean;
  visibleLinesCount?: number;
  enlargeSingleEmoji?: boolean;
  typographyVariant?: TypographyProps["variant"];
  ellipsisTypographyColor?: TypographyProps["color"];
  ellipsisTypographyVariant?: TypographyProps["variant"];
  typographyColor?: TypographyProps["color"];
  ref?: React.Ref<HTMLSpanElement>;
  alwaysShowCollapsedEllipsis?: boolean;
  onChangeCollased?: (value: boolean) => void;
}

const RichTextViewer = memo(
  ({
    richText,
    className,
    ellipsis,
    initialCollapsed = true,
    visibleLinesCount = DEFAULT_VISIBLE_LINES_COUNT,
    expandable = true,
    showEllipsis = true,
    ellipsisTypographyColor = "text.tertiary",
    ellipsisTypographyVariant = "paragraphSecondary",
    typographyVariant = "paragraphSecondary",
    typographyColor,
    enlargeSingleEmoji = false,
    alwaysShowCollapsedEllipsis = false,
    onChangeCollased
  }: Props): JSX.Element | null => {
    const [collapsed, setCollapsed] = useState(initialCollapsed);

    const linkColor = "primary";

    const toggleCollapsed: MouseEventHandler = (event): void => {
      event.stopPropagation();

      setCollapsed(collapsed => {
        const newState = !collapsed;

        onChangeCollased?.(newState);

        return newState;
      });
    };

    const normalizedRichText = getNormalizedRichText(richText);
    const partsCount = normalizedRichText.parts.length;
    const textToDisplay = normalizedRichText.parts
      .map((part, index) => {
        if (part.__typename === RICH_TEXT_PART_TYPENAME.plain) {
          if (
            enlargeSingleEmoji &&
            isTextStandaloneEmoji(part.text) &&
            partsCount === 1
          ) {
            return (
              <Truncate.Atom key={`${index}_${part.text}`}>
                <RichTextEmoji native={part.text} size={EMOJI_SIZE_LARGE} />
              </Truncate.Atom>
            );
          }

          const textAndEmojis = getSeparatedTextAndEmojis(part.text);

          return textAndEmojis.map((textPart, subindex) => {
            if (isString(textPart)) {
              return textPart;
            }

            return (
              <Truncate.Atom key={`${index}_${subindex}`}>
                {textPart}
              </Truncate.Atom>
            );
          });
        }

        if (part.__typename === RICH_TEXT_PART_TYPENAME.mention) {
          return (
            <Truncate.Atom key={`${index}_${part.nickname}`}>
              <CommonLink
                {...getMentionLinkProps(part.nickname)}
                color={linkColor}
              >
                {USER_NICKNAME_PREFIX}
                {part.nickname}
              </CommonLink>
            </Truncate.Atom>
          );
        }

        if (part.__typename === RICH_TEXT_PART_TYPENAME.tag) {
          return (
            <Truncate.Atom key={`${index}_${part.hashtag}`}>
              <CommonLink {...getTagLinkProps(part.hashtag)}>
                {HASH_TAG_SYMBOL}
                {part.hashtag}
              </CommonLink>
            </Truncate.Atom>
          );
        }

        if (
          part.__typename === RICH_TEXT_PART_TYPENAME.url &&
          EXTERNAL_LINK_TYPES.has(part.urlType)
        ) {
          const normalizedUrl = getNormalizedAbsoluteUrl(part.text);

          if (!normalizedUrl) {
            return null;
          }

          return (
            <Truncate.Atom key={`${index}_${part.path}`}>
              <CommonLink
                color={linkColor}
                href={normalizedUrl}
                rel={`${LinkRelValue.nofollow} ${EXTERNAL_LINK_REL_VALUE}`}
                target="_blank"
              >
                {part.text}
              </CommonLink>
            </Truncate.Atom>
          );
        }

        if (
          part.__typename === RICH_TEXT_PART_TYPENAME.url &&
          INTERNAL_LINK_TYPES.has(part.urlType)
        ) {
          return (
            <Truncate.Atom key={`${index}_${part.path}`}>
              <CommonLink href={part.path} color={linkColor}>
                {part.text}
              </CommonLink>
            </Truncate.Atom>
          );
        }

        return null;
      })
      .filter(notEmpty);

    if (!textToDisplay.length) {
      return null;
    }

    const ellipsisToRender = (
      <RichTextViewerDefaultEllipsis
        onClick={expandable ? toggleCollapsed : undefined}
        variant={ellipsisTypographyVariant}
        color={ellipsisTypographyColor}
        clickable={expandable}
        customCollapsedEllipsis={ellipsis}
        collapsed={collapsed}
      />
    );

    return (
      <Typography variant={typographyVariant} color={typographyColor}>
        <Truncate
          lines={collapsed ? visibleLinesCount : Infinity}
          ellipsis={showEllipsis ? ellipsisToRender : <Fragment />}
        >
          <Wrapper className={className}>
            {textToDisplay}
            {(alwaysShowCollapsedEllipsis || (!collapsed && showEllipsis)) && (
              <Truncate.Atom>{ellipsisToRender}</Truncate.Atom>
            )}
          </Wrapper>
        </Truncate>
      </Typography>
    );
  }
);

RichTextViewer.displayName = "DTORichText";

export default RichTextViewer;

const Wrapper = styled.span`
  overflow-wrap: break-word;
  white-space: pre-wrap;
  word-break: break-word;
`;

const RichTextEmoji = styled(Emoji)`
  min-height: 1em;
  min-width: 1em;
  vertical-align: -3px;
`;

const CommonLink = styled(WithLink)`
  font-size: inherit;
`;
